import React, {useEffect, useState} from 'react';
import styles from './RequestForInformation.module.css';
import {useParams} from "react-router-dom";
import {
    ActivityItem,
    CommandBarButton,
    DatePicker,
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    Dropdown,
    IconButton,
    IDropdownOption,
    IModalProps,
    IStackStyles,
    IStackTokens,
    ITextFieldProps,
    Label,
    MessageBarType,
    Modal,
    PrimaryButton,
    Stack,
    StackItem,
    TextField
} from "@fluentui/react";
import strings from "../../loc/strings";
import {useAuth} from "react-oidc-context";
import {
    fetchHistoryById,
    fetchRequestInfoById,
    pdfExportResponse,
    updateRequest
} from "../../services/RequestServices/RequestInfoService";
import {CAResponseDtoError, ResponseItem} from "../../model/ResponseItem";
import InitiatorPoiFieldsComponent from "./RequestForInfoComponents/InitiatorPoiFieldsComponent";
import {initRequestForInfoItem, IRequestForInfoItem, IRequestForInfoItemError} from "../../model/RequestForInfoItem";
import {useBoolean} from "@fluentui/react-hooks";
import {HTTP_STATUS_UNAUTHORIZED} from "../../constants/HttpStatusCodes";
import {cancelOutgoingRequest, sendReminder} from "../../services/RequestServices/OutgoingRequestService";
import {WorkflowHistoryItem, WorkflowHistoryItemError} from "../../model/WorkflowHistoryItem";
import DetailsListWithPagination from "./RequestForInfoComponents/DetailsListWithPagination";
import {_responseOptions, Status} from "./RequestForInfoUtils";
import {displayMessageBar} from "../LayoutWithAuth/LayoutWithAuth";
import {Icon} from "@fluentui/react/lib/Icon";
import {getUserInfo} from "../../services/UserInfoService";
import {ROLE_CA_END_USER} from "../../constants/EiopaFnPConstants";
import {stringToDate, validateDate} from "../Utils";
import {DateProps, onChangeDate} from "../Utils";

interface RequestForInformationProps {}

const stackStylesLabels: Partial<IStackStyles> = {
    root: {
        paddingTop: '30px',
        paddingBottom: '20px'
    },
};

const themedLargeStackTokens: IStackTokens = {
    childrenGap: 's1',
    padding: '0 0 30px 0'
};
const responseStackTokens: IStackTokens = {
    childrenGap: '10',
    padding: '10'
};
const responseChildStackTokens: IStackTokens = {
    childrenGap: '30',
};
const headerTokens: IStackTokens = {
    childrenGap: '15',
};
const numericalSpacingStackTokens: IStackTokens = {
    childrenGap: 33,
    padding: 30
};
const historyDialogStyle = {
    main: {
        selectors: {
            ['@media (min-width: 480px)']: {
                width: '100%',
                maxWidth: '1100px',
            }
        },
    }
}

function RequestForInformation(props: RequestForInformationProps) {
    const { reqNo } = useParams();
    const auth = useAuth();

    let userInfo = getUserInfo(auth.user?.id_token);
    const [state, setState] = React.useState<IRequestForInfoItem>(initRequestForInfoItem());

    const [selectedResponseType, setSelectedResponseType] = useState(0);
    const [showDateField, setShowDateField] = useState(false);
    const [commentIsRequired, setCommentIsRequired] = useState(false);

    const [responseForm, setResponseForm] = useState<ResponseItem>({
        statusTypeId: 0,
        comment: '',
        postponeDate: undefined,
        formState: {
            statusTypeId: false,
            comment: false,
            postponeDate: false,
            postponeDateErrorMessage: ''
        }
    });

    // const [isPostponeDateValid, setIsPostponeDateValid] = useState(false);
    const [hasTypedDate, setHasTypedDate] = useState(false);
    const [postponeDate, setPostponeDate] = useState<Date | undefined>(undefined);
    // let [dateString, setDateString] = useState<string | undefined>(undefined);

    const [postPoneDateState, setPostPoneDateState] = useState<DateProps>({value: postponeDate, isValid: true, isFutureDate: false, isPastDate: false  });

    const [disableForm, setDisableForm] = React.useState(false);
    const [isOutgoing, setIsOutgoing] = React.useState(true);
    const [isOnEscalationPeriod, setIsOnEscalationPeriod] = React.useState(false
    );
    const [isCancelled, setIsCancelled] = React.useState(false);
    const [isCancelDisabled, setIsCancelDisabled] = React.useState(false);
    const [pageActionsDisabled, setPageActionsDisabled] = React.useState(false);

    const [workflowHistory, setWorkflowHistory] = React.useState<WorkflowHistoryItem[]>([]);
    const maxCharLimit = 4000;
    const [status, setStatus] = React.useState(_styleStatusValue(state.statusTypeId));

    const [hideSubmitDialog, { toggle: toggleHideSubmitDialog }] = useBoolean(true);
    const [hideHistoryDialog, { toggle: toggleHideHistoryDialog }] = useBoolean(true);

    const [isCancelModalOpen, setIsCancelModalOpen] = React.useState(false);
    const [isReminderModalOpen, setIsReminderModalOpen] = React.useState(false);
    const [justification, setJustification] = React.useState("");

    useEffect(() => {
        (async function () {
            try {
                const response = await fetchRequestInfoById(reqNo!, auth.user?.id_token);
                if ((response as IRequestForInfoItemError).statusCode === HTTP_STATUS_UNAUTHORIZED) {
                    displayMessageBar(MessageBarType.error, strings.newRequestForInfoUnauthorised, false);
                    setDisableForm(true);
                    setState(initRequestForInfoItem());
                }
                if (response as IRequestForInfoItem) {
                    let responseOk = response as IRequestForInfoItem;
                    setState({
                        initiatorDetails: responseOk.initiatorDetails,
                        personOfInterest: responseOk.personOfInterest,
                        requestDetails: responseOk.requestDetails,
                        statusTypeId: responseOk.statusTypeId,
                        comment: responseOk.comment,
                        postponeDate: responseOk.postponeDate,
                        isOutgoing: responseOk.isOutgoing,
                        isOnEscalationPeriod: responseOk.isOnEscalationPeriod,
                        cancelJustification: responseOk.cancelJustification ?? '',
                        cancelDate: responseOk.cancelDate ? new Date(responseOk.cancelDate) : undefined,
                        cancelledBy: responseOk.cancelledBy ?? ''
                    });
                    setResponseForm({
                        statusTypeId: responseOk.lastStatusTypeId ? responseOk.lastStatusTypeId : responseOk.statusTypeId,
                        comment: responseOk.comment,
                        postponeDate: responseOk.postponeDate,
                        formState: {
                            statusTypeId: false,
                            comment: false,
                            postponeDate: false,
                            postponeDateErrorMessage: ''
                        }
                    })
                    setPostponeDate(responseOk.postponeDate);

                    setPostPoneDateState({
                        ...postPoneDateState,
                        value: responseOk.postponeDate ? new Date(responseOk.postponeDate) : undefined,
                        isValid: !!responseOk.postponeDate,
                    })
                    // setIsPostponeDateValid(!!responseOk.postponeDate)
                    setIsOutgoing(responseOk.isOutgoing);
                    setIsOnEscalationPeriod(responseOk.isOnEscalationPeriod);
                    setIsCancelled(responseOk.statusTypeId === Status.Cancelled);
                    setIsCancelDisabled(responseOk.isOutgoing && (responseOk.statusTypeId === Status.Cancelled || responseOk.statusTypeId === Status.SystemClosed || responseOk.statusTypeId === Status.Responded || responseOk.statusTypeId === Status.Impossible || responseOk.statusTypeId === Status.Partially));
                    setShowDateField(responseOk.statusTypeId === Status.Postpone || (responseOk.statusTypeId === Status.Cancelled && responseOk.lastStatusTypeId === Status.Postpone) || (responseOk.statusTypeId === Status.SystemClosed && responseOk.lastStatusTypeId === Status.Postpone));
                    setCommentIsRequired(responseOk.statusTypeId === Status.Impossible || responseOk.statusTypeId === Status.Partially || responseOk.statusTypeId === Status.Postpone || (responseOk.statusTypeId === Status.Cancelled || responseOk.statusTypeId === Status.SystemClosed && (responseOk.lastStatusTypeId === Status.Impossible || responseOk.lastStatusTypeId === Status.Partially || responseOk.lastStatusTypeId === Status.Postpone)));
                    setStatus(_styleStatusValue(responseOk.statusTypeId));
                    setSelectedResponseType(responseOk.lastStatusTypeId ? responseOk.lastStatusTypeId : responseOk.statusTypeId);
                    setDisableForm(!(responseOk.statusTypeId === Status.InProgress || responseOk.statusTypeId === Status.Postpone) || responseOk.isOutgoing || !userInfo.roles.includes(ROLE_CA_END_USER));
                }
            } catch (error: any) {
                displayMessageBar(MessageBarType.error, "Failed to fetch request. " + error.message, false);
                setDisableForm(true);
                setState(initRequestForInfoItem());
                setIsCancelDisabled(true);
                setPageActionsDisabled(true);
            }
        })();
    }, []);

    function _styleStatusValue(fieldContent: number) {
        switch (Number(fieldContent)) {
            case Status.InProgress:
                return <span style={{color: "green"}}>{strings.statusInProgress}</span>;
            case Status.Postpone:
                return <span style={{color: "green"}}>{strings.statusPostpone}</span>;
            case Status.Impossible:
                return <span style={{color: "blue"}}>{strings.statusImpossible}</span>;
            case Status.Partially:
                return <span style={{color: "blue"}}>{strings.statusPartially}</span>;
            case Status.Responded:
                return <span style={{color: "blue"}}>{strings.statusResponded}</span>;
            case Status.SystemClosed:
                return <span style={{color: "black"}}>{strings.statusClosed}</span>;
            case Status.Cancelled:
                return <span style={{color: "black"}}>{strings.statusCancelled}</span>;
            default:
                return <></>
        }
    }

    const _handleDropDownChange = (item: IDropdownOption | undefined) => {
        if (item) {
            setSelectedResponseType(item.key as number);
            setPostponeDate(undefined);
            // setIsPostponeDateValid(false);
            setHasTypedDate(false);
            setResponseForm({...responseForm, statusTypeId: item.key as number, postponeDate: undefined, formState: {...responseForm.formState, statusTypeId: true, comment: false, postponeDate: false, postponeDateErrorMessage: ''}});

            setPostPoneDateState({
                value: null,
                isValid: false,
                errorMessage: '',
                isFutureDate: false,
                isPastDate: false
            })
            setShowDateField(item.key === Status.Postpone);
            setCommentIsRequired(item.key === Status.Impossible || item.key === Status.Partially || item.key === Status.Postpone)
        }
    };

    const _handleCommentChange = (name: string, value: string | undefined) => {
        if (!value || value.length <= 4000) {
            setResponseForm({ ...responseForm, [name]: value, formState: {...responseForm.formState, [name]: true} });
        }
    };

    function _onSubmitClicked() {
        // setResponseForm({...responseForm, formState: {...responseForm.formState, comment: true, statusTypeId: true, postponeDate: true, postponeDateErrorMessage: getErrorMessageDateNew(onFormatDate(postponeDate), true)}});
        setResponseForm({...responseForm, formState: {...responseForm.formState, comment: true, statusTypeId: true, postponeDate: true, postponeDateErrorMessage: getErrorMessageDateNew(onFormatDate(postPoneDateState.value ?? undefined), true)}});
        if (!responseForm.statusTypeId || !_responseOptions.some(option => option.key === responseForm.statusTypeId) || (commentIsRequired && !responseForm.comment) || (showDateField && (!postPoneDateState.isValid || !postPoneDateState.value))) {
            return;
        }
        toggleHideSubmitDialog();
        setResponseForm({...responseForm});
    }

    const getSubText = (statusTypeId: number) => {
        if (statusTypeId === Status.Postpone) {
            return strings.requestForInfoSubmitPostpone;
        } else if (statusTypeId === Status.Impossible) {
            return strings.requestForInfoSubmitImpossible;
        } else if (statusTypeId === Status.Partially) {
            return strings.requestForInfoSubmitPartial;
        } else {
            return strings.requestForInfoSubmitResponded;
        }
    };

    const submitDialogContentProps = {
        type: DialogType.normal,
        title: 'Submit response',
        closeButtonAriaLabel: 'Cancel',
        subText: getSubText(responseForm.statusTypeId),
        showCloseButton: true
    };

    const historyDialogContentProps = {
        type: DialogType.normal,
        title: `View history (Request no: ${state.requestDetails?.requestNo})`,
        closeButtonAriaLabel: 'Cancel',
        subText: '',
        showCloseButton: true
    };

    const modalProps: IModalProps = {
        isBlocking: true,
    };

    async function _onSubmitOkClicked() {
        setDisableForm(true);
        try {
            let responseFormCopy: ResponseItem = {...responseForm};
            if (showDateField && responseForm.postponeDate) {
                responseFormCopy.postponeDate = new Date(responseForm.postponeDate);
                const date = new Date(responseFormCopy.postponeDate?.getTime() - responseFormCopy.postponeDate?.getTimezoneOffset() * 60000);
                setResponseForm({...responseForm, postponeDate: date});
                responseFormCopy.postponeDate = date;
            }
            const response = await updateRequest(reqNo!, responseFormCopy,  auth.user?.id_token);
            if ((response as CAResponseDtoError).statusCode === HTTP_STATUS_UNAUTHORIZED) {
                displayMessageBar(MessageBarType.error, strings.newRequestForInfoUnauthorised, false);
                setDisableForm(true);
                setState(initRequestForInfoItem());
            } else {
                displayMessageBar(MessageBarType.success, strings.newResponseSubmitSuccess, false);
                setStatus(_styleStatusValue(responseForm.statusTypeId));
                setDisableForm(!(responseForm.statusTypeId === Status.InProgress || responseForm.statusTypeId === Status.Postpone));
            }
        } catch (error: any) {
            displayMessageBar(MessageBarType.error, "Failed to update request. "+error.message, false);
            setDisableForm(false);
        }
        toggleHideSubmitDialog();
    }

    function _onHistoryClick() {
        (async function () {
            const response = await fetchHistoryById(reqNo!, auth.user?.id_token);
            if ((response as WorkflowHistoryItemError).statusCode === HTTP_STATUS_UNAUTHORIZED) {
                displayMessageBar(MessageBarType.error, strings.newRequestForInfoUnauthorised, false);
                setDisableForm(true);
                setState(initRequestForInfoItem());
            }
            if (response as WorkflowHistoryItem[]) {
                let responseOk = response as WorkflowHistoryItem[];
                setWorkflowHistory(responseOk);
            }
        })();
    }

    async function _onCancelOkClicked() {
        setIsCancelDisabled(true);
        const cancelForm = {
            requestNo: state.requestDetails.id,
            justification: justification
        };
        try {
            await cancelOutgoingRequest(cancelForm, auth.user?.id_token).then((success) => {
                setState({...state, cancelJustification: justification, cancelDate: new Date(), cancelledBy: auth.user?.profile.name ?? ''});
                setIsCancelled(true);
                setResponseForm({...responseForm, statusTypeId: Status.Cancelled});
                setStatus(_styleStatusValue(Status.Cancelled));
                setIsCancelModalOpen(false);
                const messageBarType: MessageBarType = success ? MessageBarType.success : MessageBarType.error;
                const message: string = success ? strings.messageBarCancelSuccess : strings.messageBarCancelError;
                displayMessageBar(messageBarType, message, false);
            });
        } catch (error: any) {
            displayMessageBar(MessageBarType.error, error.message, false);
            setIsCancelModalOpen(false);
            setIsCancelDisabled(false);
        }
    }

    async function _onReminderOkClicked() {
        try {
            await sendReminder(reqNo, auth.user?.id_token).then((success) => {
                const messageBarType: MessageBarType = success ? MessageBarType.success : MessageBarType.error;
                const message: string = success ? strings.messageBarReminderSuccess : strings.messageBarReminderError;
                displayMessageBar(messageBarType, message, false);
            });
        } catch (error: any) {
            displayMessageBar(MessageBarType.error, error.message, false);
        }
        setIsReminderModalOpen(false);
    }


    useEffect(() => {
        if (workflowHistory.length > 0) {
            toggleHideHistoryDialog();
        }
    }, [workflowHistory]);

    const onRenderSuffix = (props?: ITextFieldProps | undefined) => {
        return  <div>{maxCharLimit - (props?.value?.length ?? 0)}</div>
    };

    function _onChangeJustification(
        event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
        newValue?: string
    ) {
        if (!newValue || newValue.length <= 4000) {
            setJustification(newValue ?? "");
        }
    }

    const onFormatDate = (date?: Date): string => {
        if (date) {
            let day = date.getDate();
            let month = date.getMonth() + 1;
            let dayString = day < 10 ? '0' + day.toString() : day.toString();
            let monthString = month < 10 ? '0' + month.toString() : month.toString();

            return dayString + '/' + monthString + '/' + date.getFullYear();
        }

        return '';
    };

    function _onPdfExportClick() {
        displayMessageBar(MessageBarType.info, strings.messageBarExportListSpinner, true);
        pdfExportResponse(reqNo!, strings.activeRequestsExport, auth.user?.id_token).then((success) => {
            const messageBarType: MessageBarType = success ? MessageBarType.success : MessageBarType.error;
            const message: string = success ? strings.messageBarExportListSuccess : strings.messageBarExportListError;
            displayMessageBar(messageBarType, message, false);
        }).catch((error) => {
            displayMessageBar(MessageBarType.error, "Failed to export request. "+error.message, false);

        });
    }
    const activityItem =
        {
            key: 1,
            activityDescription: [
                <span key={2}>{state.cancelledBy} commented</span>,
            ],
            activityIcon: <Icon iconName={'Message'} />,
            comments: [
                <span key={1}>{state.cancelJustification}</span>,
            ],
            timeStamp: onFormatDate(state.cancelDate),
        };

    const getErrorMessageComments = (value: string) => {
        return ((!responseForm.comment || responseForm.comment?.length === 0) && commentIsRequired && responseForm.formState.comment) ? 'Field is required' : '';
    };

    const getErrorMessageDropdown = (value: number) => {
        return ((!value || !_responseOptions.some(option => option.key === value)) && responseForm.formState.statusTypeId) ? 'Field is required' : '';
    };

    const getErrorMessageDateNew = (value: string | undefined, showRequired?: boolean) => {
        if (value) {
            const date = onParseDateFromString(value)
            if (date) {
                let day = date.getDate();
                let month = date.getMonth() + 1;
                let dayString = day < 10 ? '0' + day.toString() : day.toString();
                let monthString = month < 10 ? '0' + month.toString() : month.toString();

                if (dayString + '/' + monthString + '/' + date.getFullYear() !== value) {
                    // setIsPostponeDateValid(false);
                    return 'Invalid date format';
                }
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                if (date < today) {
                    // setIsPostponeDateValid(false);
                    return 'Past dates are not permitted';
                }
            } else {
                // setIsPostponeDateValid(false);
                return 'Invalid date format';
            }
        }
        return (showDateField && !value && ((responseForm.formState.postponeDate && hasTypedDate) || showRequired)) ? 'Field is required' : '';
    }

    const onParseDateFromString = (dateString: string | undefined): Date | null => {
        // setDateString(dateString);
        if (dateString) {
            const dateRegex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[012])\/\d{4}$/;
            if(dateRegex.test(dateString)) {
                const newValueParts = dateString.trim().split('/');
                const day = Math.max(1, Math.min(31, Number.parseInt(newValueParts[0], 10)));
                const month = Math.max(1, Math.min(12, Number.parseInt(newValueParts[1], 10))) - 1;
                let year = Number.parseInt(newValueParts[2], 10);
                return new Date(year, month, day);
            }
        }
        return null;
    }

    return (
        <Stack verticalAlign="space-evenly">
            <Stack>
                <Stack horizontal={true} horizontalAlign="start" verticalAlign="end" tokens={headerTokens}>
                    <StackItem>
                        <Label className={styles.BigHeader}>
                            {strings.requestForInfotab}
                        </Label>
                    </StackItem>
                    <StackItem>
                        <Label className={styles.StatusLabel}>
                            {status}
                        </Label>
                    </StackItem>
                </Stack>
                <Stack horizontal={true} horizontalAlign="end" verticalAlign="baseline">
                    <StackItem>
                        <CommandBarButton
                            text={strings.viewHistoryButtonText}
                            className={styles.CmdBarButton}
                            iconProps={{iconName: 'History'}}
                            disabled={pageActionsDisabled}
                            onClick={() => _onHistoryClick()}
                        />
                    </StackItem>
                    <Dialog
                        hidden={hideHistoryDialog}
                        onDismiss={toggleHideHistoryDialog}
                        dialogContentProps={historyDialogContentProps}
                        modalProps={modalProps}
                        styles={historyDialogStyle}
                    >
                        <DetailsListWithPagination
                            items={workflowHistory}
                            styleStatus={_styleStatusValue}
                        />
                        <DialogFooter>
                            <DefaultButton onClick={toggleHideHistoryDialog} text="Cancel" />
                        </DialogFooter>
                    </Dialog>
                    <StackItem>
                        <CommandBarButton
                            text={strings.pdfExportButtonText}
                            className={styles.CmdBarButton}
                            iconProps={{iconName: 'pdf'}}
                            disabled={pageActionsDisabled}
                            onClick={() => _onPdfExportClick()}
                        />
                    </StackItem>
                </Stack>
            </Stack>

            {isCancelled &&
                <StackItem>
                    <ActivityItem {...activityItem} key={activityItem.key} className={styles.CancelActivityItem} />
                </StackItem>
            }


            <InitiatorPoiFieldsComponent personOfInterest={state.personOfInterest}
                                         initiatorDetails={state.initiatorDetails}
                                         poiTitle={strings.requestForInfoPOITitle}
                                         isView={true}
            />

            <StackItem styles={stackStylesLabels}>
                <Label className={styles.SmallHeader}>
                    {strings.requestForInfoReqDetails}
                </Label>
            </StackItem>
            <Stack tokens={numericalSpacingStackTokens} className={styles.ReadOnlyStack} wrap>
                <Stack horizontal={true} horizontalAlign="space-between" verticalAlign="end"
                       tokens={themedLargeStackTokens}
                       className={styles.PivotLockedFieldAndSectoralDropdown}>
                    <StackItem grow={2} className={styles.SearchStackItemSecondRow}>
                        <Label>{strings.newRequestForInfoRequestDetailsNumber}</Label>
                        <TextField disabled={true} borderless value={state.requestDetails.requestNo} />
                    </StackItem>
                    <StackItem grow={2} className={styles.SearchStackItemSecondRow}>
                        <Label>{strings.requestForInfoEUSecLegBasis}</Label>
                        <TextField disabled={true} borderless value={state.requestDetails.euSecLegBasis} />
                    </StackItem>
                </Stack>
                <Stack horizontal={true} horizontalAlign="space-between" verticalAlign="end"
                       tokens={themedLargeStackTokens}
                       className={styles.PivotRequestDetailsAndReason}>
                    <StackItem className={styles.SecondRowTextFields}>
                        <Label>{strings.requestForInfoReasonForRequest}</Label>
                        <TextField multiline
                                   rows={8}
                                   disabled
                                   borderless
                                   value={state.requestDetails.reasonForRequest}
                                   className={styles.RecipientTextField}
                                   resizable={false}
                        />
                    </StackItem>
                    <StackItem className={styles.SecondRowTextFields}>
                        <Label>{strings.requestForInfoInfoRequested}</Label>
                        <TextField multiline
                                   rows={8}
                                   disabled
                                   borderless
                                   value={state.requestDetails.infoRequested}
                                   className={styles.RecipientTextField}
                                   resizable={false}
                        />
                    </StackItem>
                </Stack>
            </Stack>

            <Stack tokens={responseStackTokens} wrap>
                <StackItem>
                    <Label className={styles.SmallHeader}>
                        {strings.requestForInfoRespDetails}
                    </Label>
                    <StackItem styles={stackStylesLabels}>
                        <Label className={styles.DisclaimerLabel}>
                            {strings.requestForInfoDisclaimer}
                        </Label>
                    </StackItem>
                </StackItem>
                <Stack horizontal={true} horizontalAlign="start" tokens={responseChildStackTokens} className={styles.PivotLockedFieldAndSectoralDropdown}>
                    <StackItem className={styles.ResponseStackItem}>
                        <Dropdown label={strings.requestForInfoResponseType} required={true} className={styles.SearchStackItemThirdRow} placeholder={strings.requestForInfoDropDownPlaceholder}
                                  options={_responseOptions}
                                  disabled={disableForm}
                                  errorMessage={getErrorMessageDropdown(responseForm.statusTypeId)}
                                  selectedKey={selectedResponseType}
                                  onChange={(event, item) =>
                                      _handleDropDownChange(item)
                                  }
                                  ariaLabel={strings.requestForInfoResponseType}
                        />
                    </StackItem>
                    <StackItem className={styles.ResponseStackItem}>
                        {showDateField &&
                            <DatePicker
                                isRequired={showDateField}
                                label={strings.requestForInfoResponseDateUntil}
                                placeholder={strings.datepickerPlaceholder}
                                disabled={disableForm}
                                ariaLabel={strings.requestForInfoResponseDateUntil}
                                minDate={new Date()}
                                formatDate={(date?: Date) => onFormatDate(date)}
                                styles={{
                                    statusMessage: {
                                        display: 'none'
                                    }
                                }}
                                allowTextInput

                                value={postPoneDateState.value ? new Date(postPoneDateState.value) : undefined}
                                onSelectDate={(date) =>{
                                    let dateOnChange = onChangeDate(date, postPoneDateState);
                                    let validationDate = validateDate(dateOnChange?.toLocaleDateString('fr-BE'), false);
                                    let validationStr = validateDate(postPoneDateState.dateString, false);
                                    console.log("onSelectDate dateString: ", postPoneDateState.dateString);
                                    let validation = postPoneDateState.dateString != null && !postPoneDateState.isPastDate ? validationStr : validationDate;

                                    setPostPoneDateState({
                                        ...postPoneDateState,
                                        value: dateOnChange,
                                        ...validation
                                    });
                                    console.log("onSelectDate validation: ", validation);
                                    setResponseForm({...responseForm,
                                        postponeDate: (validation.isValid ? date : undefined) ?? undefined,
                                        formState: {...responseForm.formState, postponeDateErrorMessage:  validation.errorMessage ?? ''}});
                                    // setIsPostponeDateValid(validation.isValid);
                                    // _handleDateChange(validation.isValid ? date : null, 'postponeDate');
                                }}
                                parseDateFromString={(dateStr: string) => {
                                    postPoneDateState.dateString = dateStr;
                                    let date = stringToDate(dateStr);
                                    let setdate = date ?? undefined;
                                    let validation = validateDate(dateStr, false);
                                    setPostPoneDateState({
                                        ...postPoneDateState,
                                        value: setdate,
                                        ...validation,
                                        dateString: dateStr
                                    });
                                    setResponseForm({...responseForm,
                                        postponeDate: (validation.isValid ? date : undefined) ?? undefined,
                                        formState: {...responseForm.formState, postponeDateErrorMessage:  validation.errorMessage ?? ''}});
                                    // setIsPostponeDateValid(validation.isValid);
                                    console.log("parseDateFromString validation: ", validation);
                                    return date;
                                }}
                                textField={{
                                    onNotifyValidationResult: (errorMessage, value) => {
                                        postPoneDateState.dateString = value;
                                    },
                                    deferredValidationTime: 0,
                                    errorMessage: postPoneDateState.errorMessage ? postPoneDateState.errorMessage : responseForm.formState.postponeDateErrorMessage
                                    // validateOnFocusOut: true,
                                    // validateOnLoad: false,
                                }}
                            />
                        }
                    </StackItem>
                </Stack>
                <Stack>
                    <div style={{ position: 'relative' }}>
                        <TextField label={strings.requestForInfoResponseComments} multiline
                                   required={commentIsRequired}
                                   resizable={false}
                                   ariaLabel={strings.requestForInfoResponseComments}
                                   disabled={disableForm}
                                   className={styles.RecipientTextField}
                                   value={responseForm.comment}
                                   onChange={(event, item) =>
                                       _handleCommentChange('comment', item)
                                   }
                                   onRenderSuffix={onRenderSuffix}
                                   errorMessage={getErrorMessageComments(responseForm.comment ?? '')}
                                   validateOnFocusOut={true}
                                   styles={{
                                       fieldGroup: [{
                                           borderColor: '#C2C2C2'
                                       }],
                                       suffix: {
                                           position: 'absolute',
                                           top: '-9px',
                                           right: '17px',
                                           backgroundColor: '#ECECEC',
                                           padding: '8px 4px',
                                           border: '1px solid #C2C2C2',
                                           borderRadius: '4px',
                                           textAlign: 'center',
                                           fontSize: '12px',
                                           zIndex: 10
                                       },
                                   }}
                        />
                    </div>
                </Stack>
                <Stack horizontal={true} horizontalAlign="end" tokens={responseChildStackTokens} wrap>
                    <StackItem>
                        <DefaultButton
                            disabled={isCancelDisabled}
                            onClick={() => setIsCancelModalOpen(true)}
                            text="Cancel"
                            className={`${!isOutgoing || !userInfo.roles.includes(ROLE_CA_END_USER) ? styles.hidden : ''}`} />
                    </StackItem>
                    <StackItem>
                        <DefaultButton
                            disabled={isCancelDisabled}
                            onClick={() => setIsReminderModalOpen(true)}
                            text="Send reminder"
                            className={`${!((isOutgoing && isOnEscalationPeriod)) || !userInfo.roles.includes(ROLE_CA_END_USER) ? styles.hidden : ''}`} />
                    </StackItem>
                    <StackItem>
                        <PrimaryButton disabled={disableForm} text={strings.requestForInfoSubmitResponseButton} allowDisabledFocus onClick={_onSubmitClicked} className={`${isOutgoing ? styles.hidden : ''}`} />
                    </StackItem>
                </Stack>
                <Dialog
                    hidden={hideSubmitDialog}
                    onDismiss={toggleHideSubmitDialog}
                    dialogContentProps={submitDialogContentProps}
                >
                    <DialogFooter>
                        <PrimaryButton disabled={disableForm} onClick={_onSubmitOkClicked} ariaLabel={"Submit"} text="Submit" />
                        <DefaultButton disabled={disableForm} onClick={toggleHideSubmitDialog} text="Cancel" />
                    </DialogFooter>
                </Dialog>
                <Modal
                    isOpen={isCancelModalOpen}
                    onDismiss={() => setIsCancelModalOpen(false)}
                    className={styles.CancelModal}
                >
                    <Stack className={styles.CancelModalContainer}>
                        <StackItem className={styles.CancelModalTitleRow}>
                            <div>
                            <span className={styles.CancelModalTitle}>
                                Cancel request
                            </span>
                                <span className={styles.CancelModalSubTitle}>
                                (Request no: {state.requestDetails.requestNo})
                            </span>
                            </div>
                            <IconButton
                                style={{marginLeft: '40px'}}
                                iconProps={{iconName: "Cancel"}}
                                ariaLabel="Close popup modal"
                                onClick={() => {
                                    setIsCancelModalOpen(false);
                                    setJustification("");
                                }}
                            />
                        </StackItem>
                        <StackItem className={styles.ReminderModalTextRow}>
                        <span>
                            {strings.requestModalCancelBody}
                        </span>
                        </StackItem>
                        <StackItem className={styles.CancelModalTextField}>
                            <TextField label="Justification" value={justification} onChange={_onChangeJustification} multiline required
                                       resizable={false}
                                       ariaLabel={"Justification"}
                                       style={{minHeight: '120px'}}
                                       onRenderSuffix={onRenderSuffix}
                                       styles={{
                                           fieldGroup: [{
                                               borderColor: '#C2C2C2'
                                           }],
                                           suffix: {
                                               position: 'absolute',
                                               top: '-9px',
                                               right: '17px',
                                               backgroundColor: '#ECECEC',
                                               padding: '8px 4px',
                                               border: '1px solid #C2C2C2',
                                               borderRadius: '4px',
                                               textAlign: 'center',
                                               fontSize: '12px',
                                               zIndex: 10
                                           },
                                       }}
                            />
                        </StackItem>
                        <StackItem className={styles.CancelModalButtons}>
                            <PrimaryButton text={strings.requestModalButtonConfirm} disabled={justification === "" || justification === undefined} onClick={_onCancelOkClicked}/>
                            <DefaultButton text={strings.requestModalButtonCancel} onClick={() => {
                                setIsCancelModalOpen(false);
                                setJustification("");
                            }}/>
                        </StackItem>
                    </Stack>
                </Modal>
                <Modal
                    isOpen={isReminderModalOpen}
                    onDismiss={() => setIsReminderModalOpen(false)}
                >
                    <Stack className={styles.ReminderModalContainer}>
                        <StackItem className={styles.ReminderModalTitleRow}>
                            <div>
                            <span className={styles.ReminderModalTitle}>
                                {strings.requestModalSendReminderTitle}
                            </span>
                                <span className={styles.ReminderModalSubTitle}>
                                ({strings.requestModalSendReminderReqNoTitle}: {state.requestDetails.requestNo})
                            </span>
                            </div>
                            <IconButton
                                style={{marginLeft: '40px'}}
                                iconProps={{iconName: "Cancel"}}
                                ariaLabel="Close popup modal"
                                onClick={() => setIsReminderModalOpen(false)}
                            />
                        </StackItem>
                        <StackItem className={styles.ReminderModalTextRow}>
                        <span>
                            {strings.requestModalSendReminderBody}
                        </span>
                        </StackItem>
                        <StackItem>
                        <span>
                             {strings.requestModalSendReminderBody2}
                        </span>
                        </StackItem>
                        <StackItem className={styles.ReminderModalButtons}>
                            <PrimaryButton text={strings.requestModalButtonConfirm} onClick={_onReminderOkClicked}/>
                            <DefaultButton text={strings.requestModalButtonCancel} onClick={() => setIsReminderModalOpen(false)}/>
                        </StackItem>
                    </Stack>
                </Modal>
            </Stack>
        </Stack>
    );
}


export default RequestForInformation;